import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthContext.jsx';
import { useDocumentTitle, useLedgerQuery } from '@/hooks';
import { PageHead, Tabs, useConfirm, useToast, Skel } from '@/ui';
import {
  closeStuckPayout,
  getPayoutQueue,
  initiatePayout,
  registerManualPayout,
  retryPayout,
  settleManualPayout,
} from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import { StatusDotPill } from '@/shared';
import {
  formatCLP,
  formatChileanDateTime,
  localDateTimeToISO,
  toLocalDateTimeInput,
} from '@/lib';

import './PlataformaPagos.css';

// Pagos · Plataforma (backoffice). Payout operation for receptores, from the
// charge onward. A quote with no payin attempt does not appear. Three lanes,
// each its own server-paginated query,
// rendered as CARDS (a row's information does not fit readably in a table):
//   · Disponibles (actionable): payin SUCCESS + payout PENDING → fire
//     Shinkansen (when there is an account) or register a manual payment.
//   · Incompletos (incomplete): what failed, and what stayed in flight past the
//     staleness threshold → retry the failed one, or close the stuck one by
//     hand.
//   · Historial (history): payout PROCESSING (in flight) or SUCCESS (paid).
// Each card expands into the full detail support needs for a ticket.

const QUEUE_KEY = qk.payoutQueue.key;

// El backoffice lee estas marcas para ordenar dos pasos del mismo día y para
// cruzar una fila contra el panel del proveedor, así que llevan hora, segundos
// y la zona de Chile. Una fecha sola no contesta ninguna de las dos preguntas.
const fmtDate = (iso) => formatChileanDateTime(iso) || '—';

function availableState(d) {
  if (d.payout?.status === 'FAILED') {
    return { label: 'Payout falló', tone: 'err' };
  }
  // Un intento que reemplaza a uno fallido llega acá pendiente, con el motivo
  // del anterior todavía puesto. Decirlo en la fila evita que el operador lo
  // trate como un payout cualquiera y vuelva a fallar por lo mismo.
  if (d.payout?.failureReason) {
    return { label: 'Reintento listo para payout', tone: 'warn' };
  }
  return { label: 'Listo para payout', tone: 'warn' };
}
function historyState(d) {
  return d.payout?.status === 'SUCCESS'
    ? { label: 'Pagado', tone: 'ok' }
    : { label: 'En proceso', tone: 'pay' };
}
// Backoffice "incompletos": qué falló + la razón técnica real (failureReason).
// `payout.status` y `payout.failureReason` salen del movimiento de pago, así
// que después de un reintento dicen PENDING y null — la fila deja de estar
// incompleta y se muda a la pestaña de disponibles.
function incompleteState(d) {
  if (d.payout?.status === 'FAILED') {
    return {
      label: 'Payout falló',
      tone: 'err',
      reason: d.payout?.failureReason,
    };
  }
  if (d.payin?.status === 'FAILED') {
    return {
      label: 'Payin falló',
      tone: 'err',
      reason: d.payin?.failureReason,
    };
  }
  // Atascado por antigüedad (sin failureReason persistido).
  return {
    label: 'Atascado',
    tone: 'err',
    reason:
      d.payin?.status !== 'SUCCESS'
        ? 'Payin sin confirmar hace más de 24 h.'
        : 'Payout sin respuesta del proveedor hace más de 24 h.',
  };
}

// The card is `role="button"` with its own `onKeyDown`, which preventDefaults
// on Enter and Space to toggle the detail. An interactive child therefore stops
// both events, not only the click: a keydown that reaches the card is cancelled
// there, so the button below it never runs its activation and the card expands
// instead. Stopping the click alone leaves every action unreachable by keyboard.
const stop = (ev) => ev.stopPropagation();

// ── detalle expandible: todo lo que soporte necesita para un ticket ──
function Field({ label, value, mono }) {
  return (
    <div className="plp-field">
      <span className="plp-field__l">{label}</span>
      <span className={`plp-field__v${mono ? ' is-mono' : ''}`}>
        {value || '—'}
      </span>
    </div>
  );
}

function RowDetail({ d }) {
  return (
    <div className="plp-detail" onClick={stop}>
      <section className="plp-detail__sec">
        <h4 className="plp-detail__h">Solicitante (estudio)</h4>
        <Field label="Abogado" value={d.requester?.name} />
        <Field label="Email" value={d.requester?.email} />
        <Field label="Razón social" value={d.payer?.name} />
        <Field label="RUT" value={d.payer?.rut} mono />
        <Field label="Email facturación" value={d.payer?.email} />
        <Field label="Dirección" value={d.payer?.address} />
      </section>
      <section className="plp-detail__sec">
        <h4 className="plp-detail__h">Receptor (pagado)</h4>
        <Field label="Titular cuenta" value={d.payee?.name} />
        <Field label="RUT" value={d.payee?.rut} mono />
        <Field label="Email" value={d.payee?.email} />
        <Field
          label="Cuenta bancaria"
          value={d.payee?.hasBankAccount ? 'Registrada' : 'Sin cuenta'}
        />
      </section>
      <section className="plp-detail__sec">
        <h4 className="plp-detail__h">Pago</h4>
        <Field
          label="Monto payout"
          value={formatCLP(d.payout?.paymentAmount)}
          mono
        />
        <Field label="Honorarios" value={formatCLP(d.paidAmount)} mono />
        <Field label="Payin" value={d.payin?.status} mono />
        <Field label="Payout" value={d.payout?.status} mono />
        <Field
          label="Payin confirmado"
          value={fmtDate(d.payin?.transactionDate)}
        />
        <Field label="Payout pagado" value={fmtDate(d.payout?.paymentDate)} />
        <Field label="Falla payin" value={d.payin?.failureReason} />
        {/* El motivo sobrevive al reintento y llega junto a un estado
            pendiente: sobre esa fila habla del intento anterior, no del vivo.
            Rotularlo «Falla payout» ahí diría que el pendiente falló. */}
        <Field
          label={
            d.payout?.status === 'FAILED'
              ? 'Falla payout'
              : 'Falla del intento anterior'
          }
          value={d.payout?.failureReason}
        />
      </section>
      <section className="plp-detail__sec">
        <h4 className="plp-detail__h">Referencias</h4>
        <Field label="Quote ID" value={d.quoteId} mono />
        <Field label="Causa ID" value={d.caseId} mono />
        {/* The id a report or a message names the row by. The row already
            carries it, to key the retry and the manual close, and showing it
            saves the operator from describing the row instead. */}
        <Field label="Movimiento ID" value={d.payout?.paymentId} mono />
        <Field label="Tx payout" value={d.payout?.transactionId} mono />
        <Field
          label="Tx Shinkansen"
          value={d.payout?.shinkansenTransactionId}
          mono
        />
        <Field label="Creado" value={fmtDate(d.createdAt)} />
        <Field label="Actualizado" value={fmtDate(d.updatedAt)} />
      </section>
    </div>
  );
}

function CardField({ label, value, sub, subTone }) {
  return (
    <div className="plp-cf">
      <span className="plp-cf__l">{label}</span>
      <span className="plp-cf__v">{value || '—'}</span>
      {sub && (
        <span className={`plp-cf__s${subTone ? ` is-${subTone}` : ''}`}>
          {sub}
        </span>
      )}
    </div>
  );
}

export default function PlataformaPagos() {
  useDocumentTitle('Pagos · Plataforma');
  const { setBreadcrumb, setPills } = useOutletContext();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  // Same explicit-org rule as the reads (useLedgerQuery): the payout mutations
  // must pin X-Organization-Id instead of leaning on the orgContext singleton.
  const organizationId = useAuth()?.organizationId ?? null;

  const [selected, setSelected] = useState(() => new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [busyQuoteId, setBusyQuoteId] = useState(null);
  // Carril visible (tab). Cada tipo es su propia pestaña — apilados no se leían.
  const [tab, setTab] = useState('actionable');

  const available = useLedgerQuery({
    resourceKey: QUEUE_KEY,
    fetcher: getPayoutQueue,
    params: { filter: 'actionable' },
    pageSize: 50,
    surfaceErrors: true,
  });
  const history = useLedgerQuery({
    resourceKey: QUEUE_KEY,
    fetcher: getPayoutQueue,
    params: { filter: 'history' },
    pageSize: 50,
    surfaceErrors: true,
  });
  const incomplete = useLedgerQuery({
    resourceKey: QUEUE_KEY,
    fetcher: getPayoutQueue,
    params: { filter: 'incomplete' },
    pageSize: 50,
    surfaceErrors: true,
  });

  useEffect(() => {
    setBreadcrumb(['Pagos']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(available.items.map((d) => d.quoteId));
      const next = new Set([...prev].filter((id) => present.has(id)));
      // Same membership: return the old set, so the identity check downstream
      // does not see a change and no consumer re-renders.
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [available.items]);

  // Rereading the queue belongs to `onSettled` and not to `onSuccess`. A
  // request that fails after the server applied it, on a dropped connection or
  // a timeout, leaves the row showing its old state, and the operator acts
  // again on something already done. These four move money, so the cost of one
  // extra read on a genuine refusal is not comparable to the cost of that.
  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: qk.payoutQueue.all() }),
    [queryClient],
  );

  const shinkansenMut = useMutation({
    mutationFn: (quoteId) => initiatePayout(quoteId, { organizationId }),
    onMutate: (quoteId) => setBusyQuoteId(quoteId),
    onSuccess: () => {
      toast({ kind: 'success', message: 'Payout disparado por Shinkansen.' });
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo disparar el payout',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: () => {
      setBusyQuoteId(null);
      invalidate();
    },
  });

  // Reintento de un payout fallido: abre el intento siguiente, no reinstruye el
  // que falló. Tras el éxito la fila cambia de pestaña — su movimiento nace
  // PENDING, así que sale de «Incompletos» y entra en «Disponibles», que es
  // donde vive la acción de disparar.
  const retryMut = useMutation({
    mutationFn: (d) => retryPayout(d.payout.paymentId, { organizationId }),
    onMutate: (d) => setBusyQuoteId(d.quoteId),
    onSuccess: () => {
      toast({
        kind: 'success',
        message:
          'Intento nuevo abierto. La fila pasó a Disponibles para dispararlo.',
      });
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo abrir el intento nuevo',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: () => {
      setBusyQuoteId(null);
      invalidate();
    },
  });

  // Manual closure of a payout the provider never answered. The operator
  // answers whether the money left, and the API turns that into a status:
  // occurred settles the movement and closes the lane, not occurred fails it,
  // and a failed attempt is what the retry on this same row needs.
  const closeMut = useMutation({
    mutationFn: ({ d, outcome }) =>
      closeStuckPayout(d.payout.paymentId, outcome, { organizationId }),
    onMutate: ({ d }) => setBusyQuoteId(d.quoteId),
    onSuccess: (_res, { outcome }) => {
      toast({
        kind: 'success',
        message:
          outcome === 'OCCURRED'
            ? 'Payout cerrado como transferido. La cotización pasó al historial.'
            : 'Payout cerrado como no transferido. La fila ya admite un reintento.',
      });
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo cerrar el payout',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: () => {
      setBusyQuoteId(null);
      invalidate();
    },
  });

  // Registro de un pago que un operario hizo a mano sobre un movimiento ya
  // instruido. No es la mutación del lote: aquella entra por INSTRUCT y solo
  // sale de un movimiento pendiente, y esta parte de uno en vuelo. Liquida el
  // existente, así que la fila se muda al historial.
  const settleMut = useMutation({
    mutationFn: ({ d, paidAt }) =>
      settleManualPayout(d.payout.paymentId, { organizationId, paidAt }),
    onMutate: ({ d }) => setBusyQuoteId(d.quoteId),
    onSuccess: () => {
      toast({
        kind: 'success',
        message: 'Pago manual registrado. La cotización pasó al historial.',
      });
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo registrar el pago manual',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: () => {
      setBusyQuoteId(null);
      invalidate();
    },
  });

  const manualMut = useMutation({
    mutationFn: (quoteIds) =>
      registerManualPayout(quoteIds, { organizationId }),
    onMutate: (quoteIds) => {
      // The slot names one row. A batch of several has no single row to name,
      // so it stays empty and `anyMutationPending` is what disables.
      const isSingleRow = quoteIds.length === 1;
      const busyRow = isSingleRow ? quoteIds[0] : null;
      setBusyQuoteId(busyRow);
    },
    onSuccess: (res) => {
      const results = Array.isArray(res?.results) ? res.results : [];
      const okN = results.filter((r) => r.status === 'success').length;
      const failed = results.filter((r) => r.status !== 'success');
      // Backoffice es uso interno → mostramos el mensaje de error real (de la API).
      const detail = failed.length
        ? ` · ${failed.length} con error: ${failed
            .map((r) => r.message ?? 'error')
            .join('; ')}`
        : '';
      toast({
        kind: okN === 0 ? 'err' : failed.length ? 'warn' : 'success',
        title: 'Pago manual',
        message: `${okN} marcado(s) como pagado(s)${detail}`,
      });
      setSelected(new Set());
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo registrar el pago manual',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: () => {
      setBusyQuoteId(null);
      invalidate();
    },
  });

  const toggleOne = useCallback((quoteId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) next.delete(quoteId);
      else next.add(quoteId);
      return next;
    });
  }, []);

  const allSelected =
    available.items.length > 0 && selected.size === available.items.length;
  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const allWereSelected = prev.size === available.items.length;
      if (allWereSelected) return new Set();
      return new Set(available.items.map((d) => d.quoteId));
    });
  }, [available.items]);

  // Money leaves on these five, so while any one is in flight no row offers an
  // action: the spinner says which row is acting, this says nobody else may
  // start.
  const anyMutationPending =
    shinkansenMut.isPending ||
    manualMut.isPending ||
    retryMut.isPending ||
    closeMut.isPending ||
    settleMut.isPending;

  const toggleExpand = useCallback((quoteId) => {
    setExpandedId((openId) => {
      // Clicking the card that is already open closes it.
      if (openId === quoteId) return null;
      return quoteId;
    });
  }, []);

  const onShinkansen = useCallback(
    async (d) => {
      const ok = await confirm({
        title: 'Disparar payout por Shinkansen',
        body: (
          <>
            Se transferirá <b>{formatCLP(d.payout?.paymentAmount)}</b> a{' '}
            <b>{d.payee?.name ?? 'el receptor'}</b> ({d.caseCompoundRol}). La
            cotización queda en proceso hasta la confirmación del banco.
          </>
        ),
        confirmLabel: 'Disparar payout',
        confirmVariant: 'seal',
      });
      if (ok) shinkansenMut.mutate(d.quoteId);
    },
    [confirm, shinkansenMut],
  );

  const onRetry = useCallback(
    async (d) => {
      const ok = await confirm({
        title: 'Reintentar el payout',
        body: (
          <>
            Se abrirá un intento nuevo por{' '}
            <b>{formatCLP(d.payout?.paymentAmount)}</b> a{' '}
            <b>{d.payee?.name ?? 'el receptor'}</b> ({d.caseCompoundRol}). El
            intento que falló conserva su motivo; este todavía no transfiere
            nada, queda listo para dispararse desde Disponibles.
          </>
        ),
        confirmLabel: 'Abrir intento nuevo',
        confirmVariant: 'seal',
      });
      if (ok) retryMut.mutate(d);
    },
    [confirm, retryMut],
  );

  // The three confirmations below are written together and read together. Two
  // of them settle the movement, so the operator cannot tell them apart by
  // their outcome: each one names its subject in the first line instead. The
  // provider transferred and never reported it; nobody transferred at all; an
  // operator transferred by hand outside the provider. Naming only what happens
  // to the row would leave the choice to elimination, on a money lane.
  const onCloseOccurred = useCallback(
    async (d) => {
      const ok = await confirm({
        title: 'Registrar que el proveedor sí transfirió',
        body: (
          <>
            Se registra que <b>el proveedor transfirió</b>{' '}
            {formatCLP(d.payout?.paymentAmount)} a{' '}
            {d.payee?.name ?? 'el receptor'} ({d.caseCompoundRol}) y nunca lo
            reportó. Úsalo solo si <b>confirmaste con el banco</b> que esa
            transferencia salió del proveedor: esto <b>no mueve dinero</b>,
            cierra el carril y esa cotización no admite ningún movimiento más.
          </>
        ),
        confirmLabel: 'Sí ocurrió',
        confirmVariant: 'seal',
      });
      if (ok) closeMut.mutate({ d, outcome: 'OCCURRED' });
    },
    [confirm, closeMut],
  );

  const onCloseNotOccurred = useCallback(
    async (d) => {
      const ok = await confirm({
        title: 'Registrar que nadie transfirió',
        body: (
          <>
            Se registra que <b>no hubo transferencia</b>: ni el proveedor, que
            dejó el intento en vuelo sin responder, ni nadie por fuera. El
            intento de {formatCLP(d.payout?.paymentAmount)} a{' '}
            {d.payee?.name ?? 'el receptor'} ({d.caseCompoundRol}) queda
            fallido. Úsalo solo si <b>confirmaste con el banco</b> que la plata
            no salió. La fila queda ofreciendo <b>Reintentar</b>, que es como se
            abre el intento siguiente.
          </>
        ),
        confirmLabel: 'No ocurrió',
        confirmVariant: 'seal',
      });
      if (ok) closeMut.mutate({ d, outcome: 'NOT_OCCURRED' });
    },
    [confirm, closeMut],
  );

  // El ref guarda el valor de la fecha y no el nodo. Dos razones: el cuerpo del
  // diálogo se construye una vez, en la llamada, así que un input controlado no
  // volvería a renderizarse al tipear; y el diálogo se desmonta al confirmar,
  // así que un ref al nodo ya está en null cuando la promesa resuelve.
  const paidAtRef = useRef(null);

  const onSettleManual = useCallback(
    async (d) => {
      const now = toLocalDateTimeInput();
      paidAtRef.current = now;
      const ok = await confirm({
        title: 'Registrar que un operario pagó a mano',
        body: (
          <>
            Se registra que <b>un operario de Recepthor transfirió a mano</b>{' '}
            {formatCLP(d.payout?.paymentAmount)} a{' '}
            {d.payee?.name ?? 'el receptor'} ({d.caseCompoundRol}), por fuera
            del proveedor. Esto <b>no mueve dinero</b>: registra el pago y
            cierra el carril.
            <br />
            <br />
            La instrucción que ya salió al proveedor <b>sigue viva</b> y todavía
            puede ejecutarse: no existe forma de retirarla. Si eso ocurre, el
            receptor cobra dos veces, y resolverlo queda de tu lado, con el
            banco o con el receptor.
            <br />
            <br />
            {/* La fecha se muestra después en el panel, así que registrar el
                instante del registro sería mostrar una fecha equivocada cuando
                la transferencia fue antes. Viene con ahora puesto, que es el
                caso común, y `max` impide elegir una futura, que el API
                rechaza. */}
            <label className="plp-paidat">
              <span className="plp-paidat__l">¿Cuándo transferiste?</span>
              <input
                type="datetime-local"
                defaultValue={now}
                max={now}
                onChange={(e) => {
                  paidAtRef.current = e.target.value;
                }}
              />
            </label>
          </>
        ),
        confirmLabel: 'Registrar el pago manual',
        confirmVariant: 'seal',
      });
      if (ok) {
        settleMut.mutate({
          d,
          paidAt: localDateTimeToISO(paidAtRef.current),
        });
      }
    },
    [confirm, settleMut],
  );

  const runManual = useCallback(
    async (ids, single) => {
      if (ids.length === 0) return;
      const ok = await confirm({
        title:
          single != null
            ? 'Registrar pago manual'
            : `Registrar ${ids.length} pago(s) manual(es)`,
        body: (
          <>
            {single != null ? (
              <>
                Marcarás como pagado el payout de{' '}
                <b>{single.caseCompoundRol}</b> (
                {formatCLP(single.payout?.paymentAmount)} a{' '}
                {single.payee?.name ?? 'el receptor'}).{' '}
              </>
            ) : (
              <>
                Marcarás <b>{ids.length}</b> payout(s) como pagados.{' '}
              </>
            )}
            Se registra que <b>un operario de Recepthor transfirió a mano</b>,
            por fuera del proveedor. Úsalo solo si{' '}
            <b>esa transferencia ya salió</b>: esto <b>no mueve dinero</b>, solo
            registra el pago y cierra la cotización.
          </>
        ),
        confirmLabel: 'Registrar como pagado',
        confirmVariant: 'seal',
      });
      if (ok) manualMut.mutate(ids);
    },
    [confirm, manualMut],
  );

  const selectedTotal = useMemo(
    () =>
      available.items
        .filter((d) => selected.has(d.quoteId))
        .reduce((sum, d) => sum + (Number(d.payout?.paymentAmount) || 0), 0),
    [available.items, selected],
  );

  // ── tarjeta ──
  const renderCard = (d, mode) => {
    const st =
      mode === 'available'
        ? availableState(d)
        : mode === 'incomplete'
          ? incompleteState(d)
          : historyState(d);
    const open = expandedId === d.quoteId;
    const hasBank = Boolean(d.payee?.hasBankAccount);
    // Two different things. `isRowBusy` is which row is acting, and it drives
    // the spinner. `isBusy` disables, and it counts ANY mutation in flight:
    // `busyQuoteId` is one slot shared by the five, so acting on a second row
    // used to re-enable the first one's button mid-request, and a second click
    // sent a second POST. The database refuses the duplicate attempt, but the
    // operator got a conflict instead of the action they asked for.
    const isRowBusy = busyQuoteId === d.quoteId;
    const isBusy = isRowBusy || anyMutationPending;
    // The same position showed creation in one lane and last movement in
    // another, unlabelled, so two rows read as comparable when they were not.
    // Each lane keeps its own field and gains a label naming it. Historial
    // carries the in-flight payout beside the paid one, so `Cerrado` is
    // reserved for SUCCESS and the rest reads as the movement it is.
    const dateField =
      mode === 'available'
        ? { iso: d.createdAt, label: 'Creado' }
        : mode === 'incomplete'
          ? { iso: d.updatedAt, label: 'Último movimiento' }
          : d.payout?.status === 'SUCCESS'
            ? { iso: d.updatedAt, label: 'Cerrado' }
            : { iso: d.updatedAt, label: 'Último movimiento' };
    // Disparar y registrar por lote pasan los dos por INSTRUCT, que solo sale
    // de PENDING: la máquina no declara salida desde FAILED ni desde
    // PROCESSING, a propósito, para que el intento conserve su motivo y su
    // historia. O sea que esas dos solo pueden prosperar en «Disponibles», que
    // es la cola de los pendientes.
    //
    // Registrar un pago a mano sobre un movimiento ya instruido sí puede, por
    // su propia ruta, que parte de uno en vuelo. Esa vía es `canSettleManual`
    // más abajo y no pasa por esta guarda.
    const canPay = mode === 'available';
    // Un payout fallido no se reinstruye: se abre el intento siguiente. Pide el
    // id del movimiento, que es lo que el reintento nombra.
    const canRetry =
      mode === 'incomplete' &&
      d.payout?.status === 'FAILED' &&
      Boolean(d.payout?.paymentId);
    // A payout in flight that the provider never answered has no other exit:
    // the machine declares none from PROCESSING except by webhook. Both actions
    // are offered only on a row this lane already brought, and the lane is what
    // applies the staleness threshold, so belonging to «Incompletos» with the
    // payout in flight IS being past it. The view does not repeat that
    // arithmetic and therefore cannot contradict the gate that enforces it.
    const canClose =
      mode === 'incomplete' &&
      d.payout?.status === 'PROCESSING' &&
      Boolean(d.payout?.paymentId);
    // El registro del pago a mano comparte esa condición y no replica ningún
    // umbral: su ruta no lo exige, porque no decide sobre lo que hizo el
    // proveedor sino que registra lo que un operario ya hizo. Se ofrece donde
    // el problema se ve, que es la fila atascada, y no sobre cualquier
    // movimiento en vuelo.
    const canSettleManual = canClose;
    return (
      <article
        key={d.quoteId}
        className={`plp-card${open ? ' is-open' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => toggleExpand(d.quoteId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpand(d.quoteId);
          }
        }}
      >
        <div className="plp-card__head">
          <div className="plp-card__lead">
            {mode === 'available' && (
              <input
                type="checkbox"
                aria-label={`Seleccionar ${d.caseCompoundRol}`}
                checked={selected.has(d.quoteId)}
                onChange={() => toggleOne(d.quoteId)}
                onClick={stop}
                onKeyDown={stop}
              />
            )}
            <span className="plp-date">
              <span className="plp-date__l">{dateField.label}</span>
              {fmtDate(dateField.iso)}
            </span>
            <StatusDotPill status={st.tone} label={st.label} />
          </div>
          <div className="plp-card__right">
            <span className="plp-money">
              {formatCLP(d.payout?.paymentAmount)}
            </span>
            {canPay && (
              <div className="plp-actions" onClick={stop} onKeyDown={stop}>
                <button
                  type="button"
                  className="plp-rowbtn"
                  disabled={!hasBank || isBusy}
                  title={
                    hasBank
                      ? 'Transferir por Shinkansen'
                      : 'Sin cuenta bancaria: usa pago manual'
                  }
                  onClick={() => onShinkansen(d)}
                >
                  {isRowBusy ? '…' : 'Shinkansen'}
                </button>
                <button
                  type="button"
                  className="plp-rowbtn plp-rowbtn--ghost"
                  disabled={isBusy}
                  title="Marcar como pagado fuera del sistema"
                  onClick={() => runManual([d.quoteId], d)}
                >
                  Manual
                </button>
              </div>
            )}
            {canRetry && (
              <div className="plp-actions" onClick={stop} onKeyDown={stop}>
                <button
                  type="button"
                  className="plp-rowbtn"
                  disabled={isBusy}
                  title="Abrir el intento siguiente de este payout"
                  onClick={() => onRetry(d)}
                >
                  {isRowBusy ? '…' : 'Reintentar'}
                </button>
              </div>
            )}
            {canClose && (
              // Three exits on one row, and two of them end in the same
              // status. The label names who transferred, which is the only
              // thing that tells them apart for the operator; which status each
              // one writes is the API's to decide.
              <div className="plp-actions" onClick={stop} onKeyDown={stop}>
                <button
                  type="button"
                  className="plp-rowbtn"
                  disabled={isBusy}
                  title="El proveedor transfirió y no lo reportó: cierra el carril"
                  onClick={() => onCloseOccurred(d)}
                >
                  {isRowBusy ? '…' : 'Transfirió el proveedor'}
                </button>
                {canSettleManual && (
                  <button
                    type="button"
                    className="plp-rowbtn"
                    disabled={isBusy}
                    title="Un operario transfirió a mano, por fuera del proveedor"
                    onClick={() => onSettleManual(d)}
                  >
                    Pagó un operario
                  </button>
                )}
                <button
                  type="button"
                  className="plp-rowbtn plp-rowbtn--ghost"
                  disabled={isBusy}
                  title="No transfirió nadie: habilita el reintento"
                  onClick={() => onCloseNotOccurred(d)}
                >
                  No transfirió nadie
                </button>
              </div>
            )}
            <span className="plp-card__chev" aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
          </div>
        </div>

        <div className="plp-card__fields">
          <CardField
            label="Diligencia"
            value={d.caseCompoundRol}
            sub={d.tribunalName}
          />
          <CardField
            label="Solicitante"
            value={d.requester?.name}
            sub={d.requester?.email}
          />
          <CardField
            label="Receptor"
            value={d.payee?.name}
            sub={d.payee?.rut ?? (!hasBank ? 'sin cuenta' : null)}
            subTone={!d.payee?.rut && !hasBank ? 'err' : undefined}
          />
          {mode === 'incomplete' && (
            <CardField
              label="Motivo (técnico)"
              value={st.reason}
              subTone="err"
            />
          )}
        </div>

        {open && (
          <div className="plp-card__detail">
            <RowDetail d={d} />
          </div>
        )}
      </article>
    );
  };

  const loadMore = (q) =>
    q.hasNextPage ? (
      <div className="plp-more">
        <button
          type="button"
          className="plp-morebtn"
          disabled={q.isFetchingNextPage}
          onClick={() => q.fetchNextPage()}
        >
          {q.isFetchingNextPage
            ? 'Cargando…'
            : `Cargar más (${q.items.length} de ${q.total})`}
        </button>
      </div>
    ) : null;

  // En una vista de plata, "vacío" y "falló la carga" no deben confundirse.
  const errorState = (q) => (
    <div className="plp-empty is-err">
      No se pudo cargar la cola.{' '}
      <button type="button" className="plp-morebtn" onClick={() => q.refetch()}>
        Reintentar
      </button>
    </div>
  );

  // Cuerpo de un carril (error / cargando / tarjetas+paginación / vacío).
  const laneBody = (q, mode, emptyMsg) => {
    if (q.isError) return errorState(q);
    if (q.isLoading)
      return (
        <div className="plp-cards" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="plp-card" style={{ pointerEvents: 'none' }}>
              <div className="plp-card__head">
                <div className="plp-card__lead" style={{ gap: 12 }}>
                  <Skel w={60} />
                  <Skel w={80} />
                </div>
                <div className="plp-card__right">
                  <Skel w={70} />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    if (!q.items.length) return <div className="plp-empty">{emptyMsg}</div>;
    return (
      <>
        <div className="plp-cards">
          {q.items.map((d) => renderCard(d, mode))}
        </div>
        {loadMore(q)}
      </>
    );
  };

  const tabItems = [
    {
      key: 'actionable',
      label: 'Disponibles',
      count: available.total,
      active: tab === 'actionable',
    },
    {
      key: 'incomplete',
      label: 'Incompletos',
      count: incomplete.total,
      active: tab === 'incomplete',
    },
    {
      key: 'history',
      label: 'Historial',
      count: history.total,
      active: tab === 'history',
    },
  ];

  return (
    <div className="page reveal">
      <PageHead
        kicker="Plataforma"
        title="Pagos"
        sub={
          <>
            Payouts a receptores · {available.total} disponible(s) ·{' '}
            {incomplete.total} incompleto(s) · {history.total} en historial
          </>
        }
      />

      <div className="plp-tabs">
        <Tabs
          items={tabItems}
          onChange={(_i, item) => {
            setTab(item.key);
            setExpandedId(null);
            // La selección (lote → pago manual) solo aplica a Disponibles.
            if (item.key !== 'actionable') setSelected(new Set());
          }}
        />
      </div>

      <section className="plp-franja">
        {tab === 'actionable' && (
          <>
            {available.items.length > 0 && (
              <div className="plp-franja__cap">
                <label className="plp-selall" onClick={stop}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                  Seleccionar todos
                </label>
              </div>
            )}
            {laneBody(
              available,
              'available',
              'No hay payouts disponibles. Cuando un payin se confirme, la cotización aparece aquí lista para pagar al receptor.',
            )}
          </>
        )}
        {tab === 'incomplete' &&
          laneBody(
            incomplete,
            'incomplete',
            'Sin pagos incompletos: nada fallido ni atascado.',
          )}
        {tab === 'history' &&
          laneBody(
            history,
            'history',
            'Aún no hay payouts en proceso ni pagados.',
          )}
      </section>

      {selected.size > 0 && (
        <div
          className="plp-selbar"
          role="region"
          aria-label="Acciones de selección"
        >
          <span className="plp-selbar__txt">
            <b>{selected.size}</b> seleccionado(s) · {formatCLP(selectedTotal)}
          </span>
          <span className="plp-selbar__sp" />
          <button
            type="button"
            className="plp-selbar__ghost"
            onClick={() => setSelected(new Set())}
          >
            Limpiar
          </button>
          <button
            type="button"
            className="plp-selbar__seal"
            disabled={anyMutationPending}
            onClick={() => runManual([...selected])}
          >
            {manualMut.isPending ? 'Registrando…' : 'Registrar pago manual'}
          </button>
        </div>
      )}
    </div>
  );
}
